import { ConsentPolicy, CookieCategory } from "../consent";

import { ActionButton } from "constants/actionButtons";
import { ScriptIntegrationMethod } from "../scriptIntegrationMethods";
import { AccessibilityBlockLeavingType } from "types/accessibility";
import {
  ITCFSelectedGatpList,
  ITCFSelectedVendorList,
} from "helpers/tcf/TCFConsentProvider/types";

export interface IWidgetConfigFile {
  baseConsent: IConfigFileBaseConsent;
  preConsent: IConfigFileBaseConsent;
  lastConsentReset: number | null;
  bulkConsent: IBulkConfig;
  consentPolicy: ConsentPolicy;
  cookieCategories: CookieCategory[];
  googleConsentModeEnabled: boolean;
  amazonConsentSignalEnabled: boolean;
  microsoftConsentModeEnabled: boolean;
  microsoftClarityEnabled: boolean;
  disableGCMUpdateEvents: boolean;
  banner_continue_button_type: ContinueButtonStyle;
  hideOutsideEU: boolean;
  hideCookiePolicy?: boolean;
  tabsOnSettingsPanel: Array<PanelTab>;
  languages: IConfigFileLang[];
  loadInlineScripts: boolean;
  okAcceptsAll: boolean;
  bannerToggles: boolean;
  privacyPolicyUrl: string;
  additionalLink: string;
  scripts: IConfigFileScript[];
  showPrivacyUrlInBanner: boolean;
  showNumberOfScriptsInBanner: boolean;
  showLanguageSwitcher: boolean;
  loadCssWithLink: boolean;
  whitelabel: {
    banner: boolean;
    panel: boolean;
    logoUrl: string | null;
    linkUrl: string | null;
  };
  denyBtn: "visible" | "text" | "hidden";
  denyBtnSecond: "visible" | "text" | "hidden";
  backdropEnabled: boolean;
  backdropColor: string;
  widget: WidgetPlacementConfig & {
    fontFamily: string;
    buttonsOrder: ActionButtonConfig[];
    /** Banner (wide) widget width; used with --banner-width in styles */
    width: string;
    /** Box widget width; used with --banner-box-width in styles when type is box */
    boxWidth?: string;
  };
  floatingBtn: {
    isEnabled: boolean;
    icon: string;
    location: FloatingBtnWidgetLocation;
  };
  focusBannerFirst: boolean;
  reconsent: {
    after: number;
    check: "ALL" | "ANY";
    categories: CookieCategory[];
  };
  version: ConfigFilesVersion;
  consentLifetime: number;
  increaseLocationPrecision: boolean;
  branding: {
    panel: IBrandingSettings;
    banner: IBrandingSettings;
  };
  bannerNonEuCountries: string[];
  tcfEnabled: boolean;
  tcfLanguages: string[];
  tcfCountry: string;
  tcfVendors: ITCFSelectedVendorList;
  tcfGdprApplies: boolean;
  acEnabled: boolean;
  acVendors: ITCFSelectedGatpList;
  bannerNonEuRegions: string[];
  gpcAndDntEnabled: boolean;
  isUsingWildcard: boolean;
  wildcardUrls: string[];
  showSuffixOnFirstLayer: boolean;
  isInPreviewMode: boolean;
  siteBaseUrl: string;
  accessibilityBlockLeaving: AccessibilityBlockLeavingType;
}

export interface IBulkConfig {
  id: string;
  group: string;
  domains: string[];
  iframeUrl: string;
  baseDomain: string;
}
export interface IBrandingSettings {
  show: boolean;
  url: string;
  logo: string;
  showLabel: boolean;
  label: string;
}

export type ConfigFilesVersion = string | null;

export interface IConfigFileScript {
  id: string;
  unique_service_uuid: string;
  name: string;
  logo: string;
  //logo_path is a temporary solution, as of 5.03.2021 its value should be moved to logo attribute, because of cached banners on user side.
  logo_path: string;
  privacy_policy_url: string;
  privacy_settings_url: string;
  categories: CookieCategory[];
  content: string;
  consent_key: string;
  integration: ScriptIntegrationMethod;
  autoblock?: {
    urls: string[];
  };
}

export interface IConfigFileBaseConsent {
  necessary: true;
  advertising: boolean;
  functional: boolean;
  performance: boolean;
}

export interface IConfigFileLang {
  value: string;
  label: string;
}

export const WIDGET_TYPE_BOX = "box";
export const WIDGET_TYPE_BANNER = "banner";
export type WidgetType = typeof WIDGET_TYPE_BOX | typeof WIDGET_TYPE_BANNER;

export type BoxXLocation = "left" | "center" | "right";
export type BoxYLocation = "top" | "center" | "bottom";

export const BOX_WIDGET_LOCATION_TOP_LEFT = "top-left";
export const BOX_WIDGET_LOCATION_TOP_RIGHT = "top-right";
export const BOX_WIDGET_LOCATION_BOTTOM_LEFT = "bottom-left";
export const BOX_WIDGET_LOCATION_BOTTOM_RIGHT = "bottom-right";
export const BOX_WIDGET_LOCATION_CENTER_CENTER = "center-center";
export const BOX_WIDGET_LOCATIONS = [
  BOX_WIDGET_LOCATION_TOP_LEFT,
  BOX_WIDGET_LOCATION_TOP_RIGHT,
  BOX_WIDGET_LOCATION_BOTTOM_LEFT,
  BOX_WIDGET_LOCATION_BOTTOM_RIGHT,
  BOX_WIDGET_LOCATION_CENTER_CENTER,
];
export type BoxWidgetLocation =
  | typeof BOX_WIDGET_LOCATION_TOP_LEFT
  | typeof BOX_WIDGET_LOCATION_TOP_RIGHT
  | typeof BOX_WIDGET_LOCATION_BOTTOM_LEFT
  | typeof BOX_WIDGET_LOCATION_BOTTOM_RIGHT
  | typeof BOX_WIDGET_LOCATION_CENTER_CENTER;

export type FloatingBtnWidgetLocation =
  | typeof BOX_WIDGET_LOCATION_TOP_LEFT
  | typeof BOX_WIDGET_LOCATION_TOP_RIGHT
  | typeof BOX_WIDGET_LOCATION_BOTTOM_LEFT
  | typeof BOX_WIDGET_LOCATION_BOTTOM_RIGHT;

export const BANNER_WIDGET_LOCATION_TOP = "top";
export const BANNER_WIDGET_LOCATION_BOTTOM = "bottom";
export type BannerWidgetLocation =
  | typeof BANNER_WIDGET_LOCATION_TOP
  | typeof BANNER_WIDGET_LOCATION_BOTTOM;

export const WidgetLocations = {
  [WIDGET_TYPE_BOX]: BOX_WIDGET_LOCATIONS as BoxWidgetLocation[],
  [WIDGET_TYPE_BANNER]: [
    BANNER_WIDGET_LOCATION_TOP,
    BANNER_WIDGET_LOCATION_BOTTOM,
  ] as BannerWidgetLocation[],
};

export type WidgetPlacementConfig =
  | { type: typeof WIDGET_TYPE_BOX; location: BoxWidgetLocation }
  | { type: typeof WIDGET_TYPE_BANNER; location: BannerWidgetLocation };

export type ActionButtonConfig = {
  value: ActionButton;
  width: "25%" | "50%" | "75%" | "100%";
};

export const PANEL_SETTINGS_TAB = "settings";
export const PANEL_COOKIES_TAB = "cookies";
export const PANEL_POLICY_TAB = "policy";
export const PANEL_DATA_TAB = "data";
export const PANEL_VENDORS_TAB = "vendors";

export type PanelTab =
  | typeof PANEL_SETTINGS_TAB
  | typeof PANEL_COOKIES_TAB
  | typeof PANEL_POLICY_TAB
  | typeof PANEL_DATA_TAB;

export enum ConsentTab {
  CATEGORIES = "categories",
  VENDORS = "vendors",
}

export const CONTINUE_BTN_STYLE_X_BTN = "x_sign";
export const CONTINUE_BTN_STYLE_X_BTN_SECONDARY = "x_sign_secondary";
export const CONTINUE_BTN_STYLE_X_ONLY = "x_icon_only";
export const CONTINUE_BTN_STYLE_BUTTON_PRIMARY = "button";
export const CONTINUE_BTN_STYLE_BUTTON_SECONDARY = "button_secondary";
export const CONTINUE_BTN_STYLE_LINK = "link";
export const CONTINUE_BTN_STYLE_DISABLED = "disabled";
export type ContinueButtonStyle =
  | typeof CONTINUE_BTN_STYLE_X_BTN
  | typeof CONTINUE_BTN_STYLE_X_BTN_SECONDARY
  | typeof CONTINUE_BTN_STYLE_X_ONLY
  | typeof CONTINUE_BTN_STYLE_BUTTON_PRIMARY
  | typeof CONTINUE_BTN_STYLE_BUTTON_SECONDARY
  | typeof CONTINUE_BTN_STYLE_LINK
  | typeof CONTINUE_BTN_STYLE_DISABLED;
